import type { ExecutionProgressHydration } from '../executionOperations/types';
import type { HumanizedCargoMeta } from '../routes/execution/cargoHumanization';
import { isLoadingStop } from '../executionOperations/executionHelpers';
import {
  buildEffectiveExecutionView,
  getPartialUnloadProjection
} from '../routes/execution/routeExecutionProjectionFacade';
import {
  getExecutionStatusLabel,
  normalizeCargoOperationalState,
  normalizeOperationalState
} from '../routes/execution/statusFacade';
import type { ExecutionIncident } from '../routes/execution/incidentFacade';
import { getIncidentTypeLabel } from '../routes/execution/incidentFacade';
import type { OperationalAttention } from '../operationalDomain/processes/operationalProcessTypes';
import { formatQtyPlaces, cargoStateLabel, DRIVER_PLACEHOLDER_LABELS } from './operationalCockpitLabels';
import {
  buildRouteOperationalIntelligence,
  isOperationalIntelligenceMode
} from '../operationalIntelligence';
import type { RouteOperationalIntelligence } from '../operationalIntelligence/types';
import {
  buildRouteOperationalCoordination,
  isOperationalCoordinationMode
} from '../operationalCoordination';
import type { CoordinationFleetContext } from '../operationalCoordination/types';
import {
  buildRouteOperationalRecovery,
  buildUnifiedOperationalTimeline,
  isOperationalRecoveryMode
} from '../operationalRecovery';
import { mapCoordinationHydrationToFleet } from '../operationalCoordination/mapCoordinationHydration';
import { preferBackendCoordinationHydration } from '../operationalRecovery/operationalRecoveryStabilizationFlags';
import type { RouteOperationalRecovery } from '../operationalRecovery/types';
import type {
  CargoFlowRow,
  CurrentOperationModel,
  DriverContextPlaceholder,
  LiveVehicleLoadModel,
  OperationalCockpitModel,
  OperationalTimelineItem,
  RouteNarrativeSegment
} from './types';
import { isSequentialStopsReadModeEnabled } from '../sequentialStops/sequentialStopsReadFlags';
import {
  logSequentialStopsReadShadow,
  sortStopsByPosition
} from '../sequentialStops/sequentialStopsOrdering';

function stopCity(stop: { city?: string | null; address?: string | null }): string {
  const city = stop.city?.trim();
  if (city) return city;
  const addr = stop.address?.trim();
  if (addr) return addr.split(',')[0]?.trim() || addr;
  return 'Точка';
}

function stopLabelByUid(
  stops: ExecutionProgressHydration['stops'],
  uid: string
): string {
  const idx = stops.findIndex((s) => s.executionStopUid === uid);
  if (idx < 0) return '—';
  const s = stops[idx];
  return `${isLoadingStop(s.type) ? 'Погрузка' : 'Выгрузка'} ${idx + 1}`;
}

export function buildRouteNarrative(
  hydration: ExecutionProgressHydration,
  effectiveStops: ExecutionProgressHydration['stops'],
  effectiveCargo: ExecutionProgressHydration['cargo']
): RouteNarrativeSegment[] {
  const segments: RouteNarrativeSegment[] = [];
  const sorted = isSequentialStopsReadModeEnabled()
    ? sortStopsByPosition(effectiveStops)
    : [...effectiveStops].sort((a, b) => a.stopId - b.stopId);

  if (isSequentialStopsReadModeEnabled()) {
    logSequentialStopsReadShadow({
      routeId: hydration.routeId,
      context: 'buildRouteNarrative',
      stops: effectiveStops
    });
  }

  for (let i = 0; i < sorted.length; i++) {
    const stop = sorted[i];
    const loading = isLoadingStop(stop.type);
    const atStop = effectiveCargo.filter((c) =>
      loading ? c.fromExecutionStopUid === stop.executionStopUid : c.toExecutionStopUid === stop.executionStopUid
    );

    const loadedQty = atStop.reduce((s, c) => s + c.loadedQty, 0);
    const unloadedQty = atStop.reduce((s, c) => s + c.unloadedQty, 0);
    const remainingQty = atStop.reduce((s, c) => s + Math.max(0, c.loadedQty - c.unloadedQty), 0);

    const detailLines: string[] = [];
    let statusLine = 'Ожидает';

    if (loading) {
      if (stop.isCompleted || loadedQty > 0) {
        statusLine = `Погружено: ${formatQtyPlaces(loadedQty)}`;
      } else if (stop.completionPercent > 0) {
        statusLine = 'Идёт погрузка';
        detailLines.push(`Догруз: ${formatQtyPlaces(loadedQty || 0)}`);
      }
    } else {
      const partial = atStop.some((c) => c.isPartiallyDelivered);
      if (partial || (unloadedQty > 0 && remainingQty > 0)) {
        statusLine = `Частично выгружено: ${formatQtyPlaces(unloadedQty)}`;
        detailLines.push(`Осталось: ${formatQtyPlaces(remainingQty)}`);
      } else if (stop.isCompleted || atStop.every((c) => c.isDelivered)) {
        statusLine = `Выгружено: ${formatQtyPlaces(unloadedQty || loadedQty)}`;
      } else if (unloadedQty > 0) {
        statusLine = `Выгружено: ${formatQtyPlaces(unloadedQty)}`;
      } else {
        statusLine = 'Ожидает выгрузку';
      }
    }

    const isLast = i === sorted.length - 1;
    const nextIncomplete = sorted.slice(i + 1).some((s) => !s.isCompleted);
    const connectorAfter =
      !isLast && (stop.isCompleted || loadedQty > 0)
        ? nextIncomplete
          ? '↓ В пути'
          : null
        : null;

    segments.push({
      key: stop.executionStopUid,
      city: stopCity(stop),
      address: stop.address,
      stopType: loading ? 'loading' : 'unloading',
      statusLine,
      detailLines,
      connectorAfter
    });
  }

  return segments;
}

export function buildLiveVehicleLoad(
  hydration: ExecutionProgressHydration,
  effectiveCargo: ExecutionProgressHydration['cargo'],
  cargoIndex: Map<number, HumanizedCargoMeta>
): LiveVehicleLoadModel {
  const vl = hydration.routeExecutionProjection?.vehicleLoad;
  const inVehiclePlaces =
    vl != null
      ? Math.max(0, vl.totalLoaded - vl.totalUnloaded)
      : effectiveCargo.reduce((s, c) => s + Math.max(0, c.loadedQty - c.unloadedQty), 0);

  const deliveredPlaces = effectiveCargo
    .filter((c) => c.isDelivered)
    .reduce((s, c) => s + c.unloadedQty, 0);

  const plannedPlaces = vl?.totalPlanned ?? effectiveCargo.reduce((s, c) => s + c.plannedQty, 0);

  let weightKg = 0;
  let volumeM3 = 0;
  for (const c of effectiveCargo) {
    const h = cargoIndex.get(c.cargoId);
    if (!h) continue;
    const share = c.plannedQty > 0 ? Math.max(0, c.loadedQty - c.unloadedQty) / c.plannedQty : 0;
    if (h.weightKg) weightKg += h.weightKg * share;
    if (h.volumeM3) volumeM3 += h.volumeM3 * share;
  }

  return {
    inVehiclePlaces,
    deliveredPlaces,
    remainingPlaces: inVehiclePlaces,
    plannedPlaces,
    weightKg: weightKg > 0 ? Math.round(weightKg) : null,
    volumeM3: volumeM3 > 0 ? Math.round(volumeM3 * 100) / 100 : null,
    freePlacesEstimate:
      plannedPlaces > 0 ? Math.max(0, plannedPlaces - inVehiclePlaces - deliveredPlaces) : null
  };
}

export function buildCargoFlow(
  hydration: ExecutionProgressHydration,
  effectiveCargo: ExecutionProgressHydration['cargo'],
  cargoIndex: Map<number, HumanizedCargoMeta>
): CargoFlowRow[] {
  return effectiveCargo.map((c) => {
    const human = cargoIndex.get(c.cargoId);
    const inVehicle = c.loadedQty > 0 && !c.isDelivered;
    return {
      cargoId: c.cargoId,
      cargoName: human?.name ?? `Груз #${c.cargoId}`,
      applicationNumber: human?.applicationNumber ?? null,
      shipper: human?.clientName ?? null,
      consignee: human?.clientName ?? null,
      fromLabel: stopLabelByUid(hydration.stops, c.fromExecutionStopUid),
      toLabel: stopLabelByUid(hydration.stops, c.toExecutionStopUid),
      stateLabel: cargoStateLabel(c.executionState),
      remainingQty: Math.max(0, c.loadedQty - c.unloadedQty),
      loadedQty: c.loadedQty,
      inVehicle
    };
  });
}

const TIMELINE_KIND_MAP: Record<string, OperationalTimelineItem['kind']> = {
  loading: 'loading',
  unloading: 'unloading',
  partial: 'partial',
  transit: 'transit',
  cargo: 'transit',
  milestone: 'completion'
};

export function buildOperationalTimelineUx(
  hydration: ExecutionProgressHydration,
  incidents: ExecutionIncident[] = []
): OperationalTimelineItem[] {
  const items: OperationalTimelineItem[] = [];
  const projection = hydration.routeExecutionProjection;

  for (const entry of projection?.executionTimeline ?? []) {
    const kind = TIMELINE_KIND_MAP[entry.kind] ?? 'system';
    let label = entry.label;
    if (kind === 'loading') label = label.replace(/LOADED|PENDING_LOADING/gi, '').trim() || 'Погрузка';
    if (kind === 'unloading') label = label.replace(/UNLOADED|UNLOADING/gi, '').trim() || 'Выгрузка';
    if (entry.kind === 'partial') label = 'Частичная выгрузка';
    if (entry.kind === 'transit') label = 'В пути';
    if (entry.label.includes('завершено')) label = 'Исполнение завершено';

    items.push({
      key: entry.key,
      at: null,
      label: entry.detail ? `${label} · ${entry.detail}` : label,
      kind,
      completed: entry.completed
    });
  }

  for (const inc of incidents) {
    items.push({
      key: `inc-${inc.id}`,
      at: inc.occurredAt,
      label:
        inc.incidentType === 'downtime'
          ? 'Простой'
          : `Инцидент: ${getIncidentTypeLabel(inc.incidentType)}`,
      kind: 'incident',
      completed: inc.status === 'resolved'
    });
  }

  if (hydration.progress.executionComplete) {
    items.push({
      key: 'completion',
      at: null,
      label: 'Маршрут завершён',
      kind: 'completion',
      completed: true
    });
  }

  return items;
}

export function buildCurrentOperation(
  hydration: ExecutionProgressHydration,
  effectiveCargo: ExecutionProgressHydration['cargo'],
  cargoIndex: Map<number, HumanizedCargoMeta>,
  activeStopUid: string | null
): CurrentOperationModel | null {
  const partial = getPartialUnloadProjection(hydration);
  const activeCargo = effectiveCargo.find(
    (c) =>
      c.isPartiallyDelivered ||
      (c.loadedQty > 0 && !c.isDelivered && c.toExecutionStopUid === activeStopUid)
  );

  if (!activeCargo && !partial?.hasPartialDelivery) {
    const pendingStop = hydration.stops.find((s) => !s.isCompleted);
    if (!pendingStop) return null;
    return {
      title: getExecutionStatusLabel(normalizeOperationalState(pendingStop.executionState)),
      applicationNumber: null,
      cargoName: null,
      address: pendingStop.address,
      unloadedQty: null,
      remainingQty: null,
      loadedQty: null,
      progressPercent: pendingStop.completionPercent
    };
  }

  const cargo = activeCargo ?? effectiveCargo.find((c) => c.isPartiallyDelivered);
  if (!cargo) return null;

  const human = cargoIndex.get(cargo.cargoId);
  const stop = hydration.stops.find((s) => s.executionStopUid === cargo.toExecutionStopUid);

  const title = cargo.isPartiallyDelivered
    ? 'Частичная выгрузка'
    : getExecutionStatusLabel(normalizeCargoOperationalState(cargo.executionState));

  return {
    title,
    applicationNumber: human?.applicationNumber ?? null,
    cargoName: human?.name ?? null,
    address: stop?.address ?? stop?.city ?? null,
    unloadedQty: cargo.unloadedQty,
    remainingQty: Math.max(0, cargo.loadedQty - cargo.unloadedQty),
    loadedQty: cargo.loadedQty,
    progressPercent:
      cargo.plannedQty > 0 ? Math.round((cargo.unloadedQty / cargo.plannedQty) * 100) : 0
  };
}

export function mergeRecoveryTimeline(
  base: OperationalTimelineItem[],
  recovery: RouteOperationalRecovery | null
): OperationalTimelineItem[] {
  if (!recovery?.recoveryTimeline.length) return base;
  const recoveryItems: OperationalTimelineItem[] = recovery.recoveryTimeline.map((r) => ({
    key: r.key,
    at: null,
    label: r.label,
    kind: 'recovery',
    completed: recovery.adaptiveState.state === 'STABILIZING' || recovery.adaptiveState.state === 'NORMAL'
  }));
  return [...recoveryItems, ...base].slice(0, 24);
}

export function mergeExecutionAttentions(
  hydration: ExecutionProgressHydration,
  processAttentions: OperationalAttention[] = [],
  intelligence: RouteOperationalIntelligence | null = null,
  recovery: RouteOperationalRecovery | null = null
): OperationalAttention[] {
  const fromProjection = hydration.routeExecutionProjection?.executionAttentions ?? [];
  const seen = new Set<string>();
  const merged: OperationalAttention[] = [];
  const sources = [
    ...fromProjection,
    ...(intelligence?.healthAttentions ?? []),
    ...(recovery?.recoveryAttentions ?? []),
    ...processAttentions.filter((p) => p.source === 'execution')
  ];
  for (const a of sources) {
    if (seen.has(a.id)) continue;
    seen.add(a.id);
    merged.push(a);
  }
  return merged.slice(0, 6);
}

export function buildDriverPlaceholders(
  incidents: ExecutionIncident[] = [],
  intelligence: RouteOperationalIntelligence | null = null,
  coordination: import('../operationalCoordination/types').RouteOperationalCoordination | null = null,
  recovery: RouteOperationalRecovery | null = null
): DriverContextPlaceholder[] {
  const openDowntime = incidents.find((i) => i.incidentType === 'downtime' && i.status === 'open');
  const h = intelligence?.progression.hoursSinceLastActivity;
  return [
    {
      key: 'eta',
      label: DRIVER_PLACEHOLDER_LABELS.eta,
      value: intelligence?.eta.expectedCompletionLabel ?? null
    },
    {
      key: 'delay',
      label: 'Вероятная задержка',
      value: intelligence?.eta.delayRiskLabel ?? null
    },
    {
      key: 'downtime',
      label: DRIVER_PLACEHOLDER_LABELS.downtime,
      value: openDowntime
        ? `${openDowntime.downtimeHours ?? '?'} ч`
        : null
    },
    { key: 'roadside', label: DRIVER_PLACEHOLDER_LABELS.roadside, value: null },
    {
      key: 'waiting',
      label: DRIVER_PLACEHOLDER_LABELS.waiting,
      value: null
    },
    {
      key: 'inactivity',
      label: 'Без активности',
      value: h != null ? `${Math.round(h)} ч` : null
    },
    {
      key: 'coord_delay',
      label: 'Влияние на цепочку',
      value: coordination?.vehicleChain.summary ?? null
    },
    {
      key: 'coord_driver',
      label: 'Давление на водителя',
      value: coordination?.driverImpact.summary ?? null
    },
    {
      key: 'recovery_waiting',
      label: 'Подтверждённое ожидание',
      value:
        recovery?.actions.actions.some((a) => a.code === 'controlled_waiting' && a.available)
          ? 'Да'
          : null
    },
    {
      key: 'recovery_downtime',
      label: 'Контролируемый простой',
      value: recovery?.incidentRecovery.items.find((i) => i.label === 'Простой')?.detail ?? null
    },
    {
      key: 'recovery_duration',
      label: 'Длительность recovery',
      value:
        recovery && recovery.adaptiveState.state !== 'NORMAL'
          ? recovery.stabilization.label
          : null
    },
    {
      key: 'recovery_stabilize',
      label: 'Стабилизация',
      value: recovery?.stabilization.label ?? null
    }
  ];
}

export type BuildOperationalCockpitParams = {
  hydration: ExecutionProgressHydration;
  cargoIndex: Map<number, HumanizedCargoMeta>;
  incidents?: ExecutionIncident[];
  processAttentions?: OperationalAttention[];
  activeStopUid?: string | null;
  fleetContext?: CoordinationFleetContext;
  plannedLoadingDate?: string | null;
  plannedUnloadingDate?: string | null;
};

export function buildOperationalCockpit(params: BuildOperationalCockpitParams): OperationalCockpitModel | null {
  const effective = buildEffectiveExecutionView(params.hydration, { cockpit: true });
  if (!effective?.projection) return null;

  const { stops, cargo } = effective;

  const intelligence = isOperationalIntelligenceMode()
    ? buildRouteOperationalIntelligence({
        hydration: params.hydration,
        incidents: params.incidents ?? []
      })
    : null;

  const backendFleet =
    preferBackendCoordinationHydration() && params.hydration.coordinationContext
      ? mapCoordinationHydrationToFleet(params.hydration)
      : undefined;

  const effectiveFleet = backendFleet ?? params.fleetContext;

  const coordination =
    isOperationalCoordinationMode() && effectiveFleet
      ? buildRouteOperationalCoordination({
          hydration: params.hydration,
          intelligence,
          fleet: effectiveFleet,
          plannedLoadingDate: params.plannedLoadingDate,
          plannedUnloadingDate: params.plannedUnloadingDate
        })
      : null;

  const recovery = isOperationalRecoveryMode()
    ? buildRouteOperationalRecovery({
        hydration: params.hydration,
        intelligence,
        coordination,
        incidents: params.incidents ?? []
      })
    : null;

  const baseTimeline = buildOperationalTimelineUx(params.hydration, params.incidents ?? []);
  const timeline = buildUnifiedOperationalTimeline({
    hydration: params.hydration,
    recovery,
    clientTimeline: mergeRecoveryTimeline(baseTimeline, recovery)
  });

  return {
    narrative: buildRouteNarrative(params.hydration, stops, cargo),
    vehicleLoad: buildLiveVehicleLoad(params.hydration, cargo, params.cargoIndex),
    cargoFlow: buildCargoFlow(params.hydration, cargo, params.cargoIndex),
    timeline,
    currentOperation: buildCurrentOperation(
      params.hydration,
      cargo,
      params.cargoIndex,
      params.activeStopUid ?? null
    ),
    executionAttentions: mergeExecutionAttentions(
      params.hydration,
      params.processAttentions ?? [],
      intelligence,
      recovery
    ),
    driverPlaceholders: buildDriverPlaceholders(
      params.incidents ?? [],
      intelligence,
      coordination,
      recovery
    ),
    intelligence,
    coordination,
    recovery
  };
}

/** Map route stop id → projection-based UI for RouteStructureBlock. */
export function buildStopExecutionIndex(
  hydration: ExecutionProgressHydration | null | undefined
): Map<number, { label: string; isProcessed: boolean; canLoad: boolean; canUnload: boolean }> {
  const map = new Map<number, { label: string; isProcessed: boolean; canLoad: boolean; canUnload: boolean }>();
  const effective = buildEffectiveExecutionView(hydration, { cockpit: true });
  if (!effective) return map;

  for (const stop of effective.stops) {
    const loading = isLoadingStop(stop.type);
    const state = normalizeOperationalState(stop.executionState);
    const isProcessed =
      loading
        ? state === 'loading_complete' || stop.isCompleted
        : state === 'unloading_complete' || stop.isCompleted;
    map.set(stop.stopId, {
      label: getExecutionStatusLabel(state),
      isProcessed,
      canLoad: loading && !isProcessed,
      canUnload: !loading && !isProcessed
    });
  }
  return map;
}
